import { MutableRefObject, useEffect, useMemo, useState } from "react";
import { Box, Grid, SvgIcon } from "@mui/material";
// @ts-ignore
import { StriimButton, StriimFeedbackBlock, StriimTypography, useStriimForm, StriimTooltip } from "@striim/striim-ui";

import { styles } from "./selectTables.styles";
import { InfoEdit } from "src/generic/icon/customIcons";
import { TestConnectionStatus } from "src/generic/test-connection/test-connection";
import AddQueriesModal, { QueryItem } from "./components/add-queries-modal/add-queries-modal";
import { getFormattedDate } from "./selectTables-utils";
import api from "core/api/api";
import { Tabs } from "./selectTables";
import { useSelectTables } from "./select-tables-context";

const CONNECTION_FIELDS = ["ConnectionURL", "Username", "Password"];

interface ContentSqlInterface {
    setSqlCode: React.Dispatch<React.SetStateAction<string>>;
    enableAction: boolean;
    testConnectionStatus: TestConnectionStatus;
    adapterName: string;
    databaseProviderType?: string;
    isCacheComponent: boolean;
    previousTab?: Tabs;
    tablesRef: MutableRefObject<{
        tab: string;
        schemas: number | null;
        entities?: number;
        timestamp: number;
        data: any[];
        queries?: QueryItem[];
        hasError?: boolean;
    } | null>;
    metaObjectId?: string;
    hasPreviousSelections?: boolean;
    onCancellingModeSwitch?: () => void;
    onSwitchToQueryMode: () => void;
}

const ContentSql: React.FC<ContentSqlInterface> = ({
    setSqlCode,
    enableAction,
    testConnectionStatus,
    adapterName,
    databaseProviderType,
    previousTab,
    tablesRef,
    metaObjectId,
    hasPreviousSelections,
    onCancellingModeSwitch,
    onSwitchToQueryMode,
    isCacheComponent
}) => {
    const { currentSelection, setCurrentSelection } = useSelectTables();
    const { values: formValues, submitCount } = useStriimForm();
    const [savedQueries, setSavedQueries] = useState<QueryItem[]>([]);

    useEffect(() => {
        setSavedQueries(currentSelection?.queries?.length ? currentSelection.queries : []);
    }, [currentSelection]);

    const areConnectionFieldsValid = useMemo(() => {
        if (formValues?.hasOwnProperty("useConnectionProfile") && !!formValues?.useConnectionProfile) {
            return formValues.hasOwnProperty("connectionProfileName") && !!formValues["connectionProfileName"];
        } else {
            return CONNECTION_FIELDS.every(field => formValues?.hasOwnProperty(field) && !!formValues[field]);
        }
    }, [formValues]);

    const [showAddQueriesModal, setShowAddQueriesModal] = useState<boolean>(false);

    const [modeSwitchDismissed, setModeSwitchDismissed] = useState<boolean>(false);

    const showModeSwitchWarning =
        !modeSwitchDismissed && hasPreviousSelections && previousTab && previousTab !== Tabs.query;

    const modeSwitchMessage =
        previousTab === Tabs.visual
            ? "You have already selected some tables visually. If you switch to the query mode you will lose your previous selections."
            : "You have already selected some tables in list mode. If you switch to the query mode you will lose your previous selections.";

    const handleAddQueries = () => {
        setShowAddQueriesModal(true);
    };

    const MAX_VISIBLE_QUERIES = 5;

    const handleSaveQueries = async (queries: QueryItem[]) => {
        setSavedQueries(queries);

        const backendFormat = queries.map(q => ({
            QueryIdentifier: q.identifier,
            Query: q.sql,
            Priority: q.priority
        }));
        const currentTimestamp = Date.now();
        setSqlCode(JSON.stringify(backendFormat));

        let hasError = false;
        try {
            const properties: Record<string, string> = {};
            Object.keys(formValues).forEach(key => {
                if (formValues[key] != null) {
                    properties[key] = formValues[key]?.value ?? formValues[key];
                }
            });
            const querySqls = queries.map(q => q.sql);
            const response = await api.validateSelectQueries(querySqls, properties);
            const results = JSON.parse(response);
            hasError = results.some((r: { valid: boolean }) => !r.valid);
        } catch (e) {
            console.error("Failed to validate queries", e);
            hasError = true;
        }

        tablesRef.current = {
            tab: Tabs.query,
            schemas: null,
            timestamp: currentTimestamp,
            data: [],
            queries,
            hasError
        };
        setCurrentSelection(prev => ({ ...prev, hasError, queries }));
        setShowAddQueriesModal(false);
    };

    const handleEditQueries = () => {
        setShowAddQueriesModal(true);
    };

    const visibleQueries = savedQueries.slice(0, MAX_VISIBLE_QUERIES);
    const remainingCount = savedQueries.length - MAX_VISIBLE_QUERIES;

    return (
        <Grid container>
            {showModeSwitchWarning ? (
                <Grid item xs={12} mb={2}>
                    <StriimFeedbackBlock
                        variant="info"
                        content={
                            <StriimTypography variant="body4" color="greyscale.900">
                                {modeSwitchMessage}
                            </StriimTypography>
                        }
                        footer={
                            <Box display="flex" gap={2}>
                                <StriimButton
                                    variant="text"
                                    sx={styles.footerButton}
                                    onClick={() => {
                                        setModeSwitchDismissed(true);
                                        onSwitchToQueryMode();
                                    }}
                                >
                                    Switch to Query Mode
                                </StriimButton>
                                <StriimButton
                                    variant="text"
                                    sx={[styles.footerButton, styles.cancelButton]}
                                    onClick={onCancellingModeSwitch}
                                >
                                    Cancel
                                </StriimButton>
                            </Box>
                        }
                    />
                </Grid>
            ) : savedQueries.length > 0 ? (
                <Grid item xs={12}>
                    <StriimFeedbackBlock
                        customStyles={{
                            body: {
                                "& > div:first-of-type": {
                                    width: "100%"
                                }
                            }
                        }}
                        variant={currentSelection.hasError ? "warning" : "success"}
                        header={getFormattedDate(currentSelection.timestamp)}
                        content={
                            <Box>
                                <Box>
                                    <StriimTypography variant="body4" color="greyscale.900" fontFamily="Inter">
                                        {savedQueries.length} {savedQueries.length === 1 ? "query" : "queries"} added.
                                        {currentSelection.hasError &&
                                            " One or more of the queries listed below may have warnings. Review if needed."}
                                    </StriimTypography>
                                </Box>
                                {visibleQueries.map(query => (
                                    <StriimTooltip title={query?.identifier}>
                                        <StriimTypography
                                            key={query.id}
                                            variant="body4"
                                            color="greyscale.900"
                                            fontFamily="Inter"
                                            sx={{
                                                pl: 0.1,
                                                display: "block",
                                                maxWidth: "90%",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                lineBreak: "none",
                                                whiteSpace: "nowrap"
                                            }}
                                        >
                                            ({query.priority}) {query.identifier}
                                        </StriimTypography>
                                    </StriimTooltip>
                                ))}
                                {remainingCount > 0 && (
                                    <StriimButton
                                        variant="text"
                                        disabled={!enableAction}
                                        onClick={handleEditQueries}
                                        sx={{ pl: 0.1, mt: 0.5 }}
                                    >
                                        + {remainingCount} More
                                    </StriimButton>
                                )}
                            </Box>
                        }
                        footer={
                            <StriimButton
                                variant="text"
                                disabled={!enableAction}
                                startIcon={
                                    <SvgIcon
                                        component={InfoEdit}
                                        sx={[styles.editIcon, !enableAction ? styles.iconDisabled : undefined]}
                                    />
                                }
                                sx={{ ...styles.footerButton, mt: 0 }}
                                onClick={handleEditQueries}
                                data-testid="edit-queries-button"
                            >
                                Edit Queries
                            </StriimButton>
                        }
                    />
                </Grid>
            ) : submitCount > 0 ? (
                <Grid item xs={12}>
                    <StriimFeedbackBlock
                        variant="critical"
                        content={
                            <StriimTypography variant="body4" color="greyscale.900">
                                Add at least one query to continue.
                            </StriimTypography>
                        }
                        footer={
                            <StriimButton variant="text" sx={styles.footerButton} onClick={handleAddQueries}>
                                Add Queries
                            </StriimButton>
                        }
                    />
                </Grid>
            ) : (
                <>
                    {enableAction && !areConnectionFieldsValid && (
                        <Grid item xs={12} mb={2}>
                            <StriimFeedbackBlock
                                variant="warning"
                                content={
                                    <StriimTypography variant="body4" color="greyscale.900">
                                        You can add queries but preview queries are available after configuring
                                        connection properties.
                                    </StriimTypography>
                                }
                            />
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <StriimTypography variant="body4" color="greyscale.800" fontFamily={"Inter"}>
                            {adapterName === "DatabaseReader" ? "Fast Snapshot Load using SQL Queries" : "Select tables using one or more SQL queries."}
                        </StriimTypography>
                    </Grid>
                    <Grid item xs={12} mt={2}>
                        <StriimButton variant="secondary" onClick={handleAddQueries} data-testid="open-add-queries">
                            Add Queries
                        </StriimButton>
                    </Grid>
                </>
            )}

            {showAddQueriesModal && (
                <AddQueriesModal
                    metaObjectId={metaObjectId}
                    isVisible={showAddQueriesModal}
                    onCancel={() => setShowAddQueriesModal(false)}
                    onSave={handleSaveQueries}
                    initialQueries={savedQueries}
                    testConnectionStatus={testConnectionStatus}
                    adapterName={adapterName}
                    databaseProviderType={databaseProviderType}
                    isCacheComponent={isCacheComponent}
                />
            )}
        </Grid>
    );
};

export default ContentSql;
